import { Country, isFeatureEnabled } from '@hconnect/apiclient'
import { Typography } from '@hconnect/uikit'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { api } from '../../../../App.store'
import { useFeaturesCheckFetcher } from '../../../../common/hooks/useFeaturesCheckFetcher'
import { countryDataByCountryCode } from '../../../../common/utils/utils'
import { useTranslation } from '../../../../hooks/useTranslation'
import { selectLoggedInUserProfile } from '../../../../modules/LoggedInUser.selectors'
import { CheckboxForm } from '../Announcements.components'

import { AnnouncementFormProps } from './types'

export type CountriesInputProps = AnnouncementFormProps & {
  countries: Country[]
}
export const CountriesInput = ({ formMethods, countries }: CountriesInputProps) => {
  const { t } = useTranslation()
  const products = formMethods.getValues('products')
  const countriesData = window.intlTelInputGlobals.getCountryData()
  const { data: features } = useFeaturesCheckFetcher(api)
  const isOnSite = products.includes('OnSite')

  const shouldExcludeCountry = (countryCode: string) => {
    return isOnSite
      ? isFeatureEnabled(features || [], { country: countryCode }, 'OnSiteAnnouncementCountries')
      : false
  }

  const countryDataFilter = (countryId?: string | null) =>
    countryDataByCountryCode(countriesData, countryId)
  countries = countries
    .filter((country) => {
      return (
        country.liveProducts.filter((value) =>
          products.includes('MHC') ? true : products.includes(value)
        ).length > 0 && !shouldExcludeCountry(country.countryCode)
      )
    })
    .sort((a, b) => a.countryName.localeCompare(b.countryName))

  return (
    <Box display="flex" flexDirection="column" data-test-id="announcement-form-countries-row">
      <Typography style={{ fontSize: '18px', fontWeight: 600 }}>Country</Typography>
      {countries.length > 0 ? (
        <Controller
          control={formMethods.control}
          name="countries"
          rules={{
            validate: (value) => value.length > 0
          }}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormGroup row style={{ marginTop: '15px' }}>
                <Grid container wrap="wrap">
                  {countries.map((country) => (
                    <Grid key={country.countryCode} item xs={12}>
                      <FormControlLabel
                        control={
                          <CheckboxForm
                            name={country.countryCode}
                            dataTestId={`${country.countryCode}-checkbox`}
                            checked={field.value.includes(country.countryCode)}
                            onChange={(
                              _: React.ChangeEvent<HTMLInputElement>,
                              checked: boolean
                            ) => {
                              const selectedCountries = new Set(field.value)
                              if (checked) {
                                selectedCountries.add(country.countryCode)
                              } else {
                                selectedCountries.delete(country.countryCode)
                              }
                              field.onChange([...selectedCountries])
                              formMethods.setValue('orgUnitIds', [])
                            }}
                          />
                        }
                        label={countryDataFilter(country.countryCode)?.name || country.countryName}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              {formMethods.formState.errors.countries?.type === 'validate' ? (
                <FormHelperText
                  data-test-id="countries-helper-text"
                  style={{ marginLeft: 0 }}
                  component="div"
                  error={formMethods.formState.errors.countries?.type === 'validate'}
                >
                  {t('announcements.form.validateCountries')}
                </FormHelperText>
              ) : null}
            </FormControl>
          )}
        />
      ) : (
        <Typography style={{ marginTop: '12px', marginLeft: '2px' }}>
          {t('announcements.form.noCountries')}
        </Typography>
      )}
    </Box>
  )
}
